import React from 'react';
import { cn } from '@/lib/utils';
import { Loader2, RefreshCw } from 'lucide-react';

/**
 * LoadingState - Standardized loading indicator
 * 
 * @param {string} message - Optional loading message
 * @param {string} size - 'sm' | 'md' | 'lg'
 * @param {boolean} fullscreen - Center in full viewport
 * @param {string} className - Additional classes
 */
export function LoadingState({
  message,
  size = 'md',
  fullscreen = false,
  className,
  ...props
}) {
  const sizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
  };

  const content = (
    <div 
      className={cn(
        "flex flex-col items-center justify-center gap-3",
        fullscreen ? "min-h-screen" : "py-16",
        className
      )}
      data-testid="loading-state"
      {...props}
    >
      <div 
        className={cn(
          "rounded-full border-2 border-slate-200 border-t-red-600 animate-spin",
          sizeClasses[size]
        )}
      />
      {message && (
        <p className="text-sm text-slate-500">{message}</p>
      )}
    </div>
  );

  return content;
}

/**
 * LoadingSpinner - Inline loading spinner
 */
export function LoadingSpinner({ size = 'sm', className }) {
  const sizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  return (
    <Loader2 
      className={cn("animate-spin", sizeClasses[size], className)} 
      data-testid="loading-spinner"
    />
  );
}

/**
 * RefreshButton - Refresh action with loading state
 */
export function RefreshButton({ 
  loading, 
  onClick, 
  className,
  ...props 
}) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={cn(
        "h-9 w-9 flex items-center justify-center rounded-lg",
        "text-slate-400 hover:text-slate-600 hover:bg-slate-100",
        "transition-colors disabled:opacity-50",
        className
      )}
      data-testid="refresh-button"
      {...props}
    >
      <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
    </button>
  );
}

export default LoadingState;
